import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { language } = useLanguage();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const handleWhatsAppClick = () => {
    if (settings?.appointment_whatsapp) {
      const number = settings.appointment_whatsapp.replace(/[^0-9]/g, '');
      const message = encodeURIComponent(language === 'en' ? "Hello, I'd like to book an appointment" : "नमस्ते, मैं अपॉइंटमेंट बुक करना चाहूँगा");
      window.open(`https://wa.me/${number}?text=${message}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white font-body" data-testid="contact-page">
      <Navbar />

      <PageHero
        kicker={language === 'en' ? "We're here to help" : 'हम मदद के लिए हैं'}
        title={language === 'en' ? 'Contact Us' : 'संपर्क करें'}
        subtitle={language === 'en' ? 'Get in touch for appointments, inquiries, or emergencies' : 'अपॉइंटमेंट, पूछताछ या आपातकालीन स्थिति के लिए संपर्क करें'}
        testId="contact-hero"
      />

      {/* Contact Grid */}
      <div className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-24 text-center">
            <div>
              <MapPin className="h-6 w-6 mx-auto mb-4 text-brand-teal" />
              <h3 className="font-heading font-light text-lg uppercase tracking-wide mb-3">
                {language === 'en' ? 'Address' : 'पता'}
              </h3>
              <p className="text-neutral-500 font-body font-light text-sm leading-relaxed">
                {language === 'en' ? settings?.address_en : settings?.address_hi}
              </p>
            </div>
            <div>
              <Phone className="h-6 w-6 mx-auto mb-4 text-brand-teal" />
              <h3 className="font-heading font-light text-lg uppercase tracking-wide mb-3">
                {language === 'en' ? 'Phone' : 'फोन'}
              </h3>
              <a href={`tel:${settings?.phone}`} className="text-neutral-500 hover:text-brand-teal transition-colors font-body font-light text-sm">
                {settings?.phone}
              </a>
            </div>
            <div>
              <Mail className="h-6 w-6 mx-auto mb-4 text-brand-teal" />
              <h3 className="font-heading font-light text-lg uppercase tracking-wide mb-3">
                {language === 'en' ? 'Email' : 'ईमेल'}
              </h3>
              <a href={`mailto:${settings?.email}`} className="text-neutral-500 hover:text-brand-teal transition-colors font-body font-light text-sm">
                {settings?.email}
              </a>
            </div>
          </div>

          {/* WhatsApp Appointment */}
          <div className="text-center mb-24">
            <h2 className="font-heading font-light text-2xl uppercase tracking-wide mb-4">
              {language === 'en' ? 'Book an Appointment' : 'अपॉइंटमेंट बुक करें'}
            </h2>
            <p className="text-neutral-500 font-body font-light text-sm mb-8 max-w-lg mx-auto">
              {language === 'en'
                ? 'Start a WhatsApp conversation to book your appointment instantly.'
                : 'तुरंत अपनी अपॉइंटमेंट बुक करने के लिए WhatsApp पर बात शुरू करें।'}
            </p>
            <button
              onClick={handleWhatsAppClick}
              className="inline-flex items-center gap-3 px-10 py-4 bg-brand-teal text-white uppercase tracking-ultra-wide text-xs font-body hover:bg-brand-teal-dark transition-colors"
              data-testid="whatsapp-appointment-btn"
            >
              <MessageCircle className="h-4 w-4" />
              {language === 'en' ? 'Enquire for Appointment' : 'अपॉइंटमेंट के लिए पूछताछ करें'}
            </button>
          </div>

          {/* Emergency */}
          <div className="bg-brand-navy text-white py-12 px-8 text-center mb-24">
            <h3 className="font-heading font-light text-xl uppercase tracking-wide mb-3">
              {language === 'en' ? 'Emergency?' : 'आपातकाल?'}
            </h3>
            <p className="text-neutral-400 font-body font-light text-sm mb-4">
              {language === 'en' ? 'For immediate medical assistance, call us at:' : 'तत्काल चिकित्सा सहायता के लिए:'}
            </p>
            <a href={`tel:${settings?.phone}`} className="text-3xl font-heading font-light tracking-wide hover:text-neutral-300 transition-colors">
              {settings?.phone}
            </a>
          </div>

          {/* Map */}
          <div>
            <h2 className="text-center font-heading font-light text-2xl uppercase tracking-wide mb-10">
              {language === 'en' ? 'Find Us' : 'हमें खोजें'}
            </h2>
            <div data-testid="location-map">
              <div className="relative w-full" style={{ paddingBottom: '66.6666%' }}>
                <iframe
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0" scrolling="no" marginHeight="0" marginWidth="0"
                  src="https://maps.google.com/maps?width=600&height=400&hl=en&q=SKB%20Aarogyam%20Multi-Speciality%20Healthcare&t=&z=14&ie=UTF8&iwloc=B&output=embed"
                  title="SKB Aarogyam Location" loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
